import React from "react";
import {
  OPTION_FIELD_TYPES,
  OVERRIDE_DECISIONS,
  SIMPLE_DECISIONS,
  FieldDecision,
  FormConfigField,
  WorkspaceTask,
} from "../shared/types";
import * as S from "../shared/styles";

interface FieldReviewTableProps {
  fields: FormConfigField[];
  isOverride: boolean;
  task: WorkspaceTask;
  decisions: Record<string, FieldDecision>;
  onDecide: (label: string, patch: Partial<FieldDecision>) => void;
  showError: boolean;
  disabled?: boolean;
}

const FieldReviewTable: React.FC<FieldReviewTableProps> = ({
  fields,
  isOverride,
  task,
  decisions,
  onDecide,
  showError,
  disabled = false,
}) => {
  const decisionOptions = isOverride
    ? OVERRIDE_DECISIONS.map((v) => ({
        value: v,
        label:
          v === "ai_correct"
            ? "AI Correct"
            : v === "human_correct"
              ? "Human Correct"
              : "Both Incorrect",
      }))
    : SIMPLE_DECISIONS.map((v) => ({
        value: v,
        label: v === "correct" ? "Correct" : "Incorrect",
      }));

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Fields</th>
            <th style={S.th}>LLM extracted</th>
            {isOverride && <th style={S.th}>Austin output</th>}
            <th style={S.th}>Review decision</th>
            <th style={S.th}>Select correct value</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => {
            const d = decisions[f.label] || {};
            const needsCorrection = d.decision === "incorrect" || d.decision === "both_incorrect";
            const missingDecision = f.mandatory && !d.decision;
            const missingCorrection = needsCorrection && !d.correction_value;
            const rowError = showError && (missingDecision || missingCorrection);
            const opts =
              (OPTION_FIELD_TYPES as readonly string[]).indexOf(f.type) !== -1
                ? f.options.filter(Boolean)
                : null;
            const correctionOpts = opts && opts.length > 0 ? opts : null;

            return (
              <tr key={f.id} style={rowError ? { background: "#fee2e2" } : undefined}>
                <td style={{ ...S.tdField, color: rowError ? "#ef4444" : "#374151" }}>
                  {f.label}
                  {f.mandatory && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                </td>
                <td style={S.td}>{task.ai_generated_values?.[f.label] ?? "\u2014"}</td>
                {isOverride && (
                  <td style={S.td}>{task.human_override_values?.[f.label] ?? "\u2014"}</td>
                )}
                <td style={S.td}>
                  <select
                    value={d.decision ?? ""}
                    disabled={disabled}
                    onChange={(e) => onDecide(f.label, { decision: e.target.value })}
                    style={{
                      ...S.formSelect,
                      borderColor: missingDecision && showError ? "#ef4444" : "#d1d5db",
                      width: "100%",
                    }}
                  >
                    <option value="">Choose output</option>
                    {decisionOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={S.td}>
                  {needsCorrection ? (
                    correctionOpts ? (
                      <select
                        value={d.correction_value ?? ""}
                        disabled={disabled}
                        onChange={(e) => onDecide(f.label, { correction_value: e.target.value })}
                        style={{
                          ...S.formSelect,
                          borderColor: missingCorrection && showError ? "#ef4444" : "#d1d5db",
                          width: "100%",
                        }}
                      >
                        <option value="">Choose output</option>
                        {correctionOpts.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={d.correction_value ?? ""}
                        disabled={disabled}
                        onChange={(e) => onDecide(f.label, { correction_value: e.target.value })}
                        placeholder="Enter correct value"
                        style={{
                          ...S.formInput,
                          borderColor: missingCorrection && showError ? "#ef4444" : "#d1d5db",
                        }}
                      />
                    )
                  ) : (
                    <select
                      disabled
                      style={{
                        ...S.formSelect,
                        background: "#f9fafb",
                        color: "#d1d5db",
                        width: "100%",
                      }}
                    >
                      <option>Choose output</option>
                    </select>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FieldReviewTable;
